import {
  BrandHandle,
  DeliveryEstimates,
  QueryMetadata,
} from 'utilities/delivery-promise/types';

import {FetchAPIError, NetworkError, QueryStorefrontAPIError} from './errors';
import {GraphQLResponse} from './types';

export const lightLogoSrc =
  'https://cdn.shopify.com/shopifycloud/shopify/assets/branded_promises/shop_promise_compact_light-f89c86213bd9730775dc2441f78cb2d6c7ae4e8c18d48c1cfb696bb6b369dc4f.svg';
export const darkLogoSrc =
  'https://cdn.shopify.com/shopifycloud/shopify/assets/branded_promises/shop_promise_compact_dark-b254936771f5a8eef112df82073042e38c027206d1e7d77a4251dc593b8e1a93.svg';
const DELIVERY_PROMISE_GRAPHQL_ENDPOINT = `/api/unstable/graphql.json`;

export async function storefrontAPIClient<TData>(
  query: string,
  storefrontAPIAccessToken: string,
) {
  const body = {
    query,
    schemaHandle: 'storefront',
    versionHandle: 'unstable',
  };

  const response = await fetch(DELIVERY_PROMISE_GRAPHQL_ENDPOINT, {
    method: 'POST',
    headers: {
      /* eslint-disable @typescript-eslint/naming-convention */
      'X-Shopify-Storefront-Access-Token': storefrontAPIAccessToken,
      'content-type': 'application/json',
      /* eslint-enable */
    },
    body: JSON.stringify(body),
  }).catch((error) => {
    throw new NetworkError(error);
  });

  if (!response.ok) {
    throw new FetchAPIError(
      `StorefrontAPI Response Status: ${response.status}`,
    );
  }

  const {data, errors} = (await response.json()) as GraphQLResponse<TData>;

  if (errors) {
    throw new QueryStorefrontAPIError(errors);
  }

  return {data, headers: response.headers};
}

export async function mockStorefrontAPIClient<TData>(
  _query: string,
  _storefrontAPIAccessToken: string,
) {
  const deliveryEstimates = createMockDeliveryEstimates(
    BrandHandle.ShopPromise,
    true,
  );
  const data = {deliveryEstimates} as TData;

  return new Promise<{data: TData; headers: Headers}>((resolve) => {
    setTimeout(() => {
      resolve({data, headers: new Headers()});
    }, 1000);
  });
}

export function createMockDeliveryEstimates(
  brandHandle: BrandHandle = BrandHandle.ShopPromise,
  includeCity = false,
): DeliveryEstimates & QueryMetadata {
  return {
    selectedShippingOption: createMockDeliveryOptions(brandHandle),
    freeDeliveryThreshold: null,
    deliveryAddress: {
      zip: '10001',
      city: includeCity ? 'New York City' : '',
      province: 'New York',
      provinceCode: 'NY',
      country: 'United States',
      countryCodeV2: 'US',
      timezone: 'America/New_York',
    },
    productHandle: 'intelligent-silk-coat',
    deliveryDateMissingReason: null,
    options: [createMockDeliveryOptions(brandHandle)],
    renderEstimates: true,
    variant: '3',
    product: '1',
    requestId: 'some_id',
  };
}

function addDaysToDate(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function createMockDeliveryOptions(
  brandHandle: BrandHandle = BrandHandle.ShopPromise,
) {
  const defaultDeliveryInDays = 1;
  const mockDeliveryInDays = new URLSearchParams(location.search).get(
    'deliveryInDays',
  );
  const deliveryInDays: number = mockDeliveryInDays
    ? parseInt(mockDeliveryInDays, 10) || defaultDeliveryInDays
    : defaultDeliveryInDays;
  const deliveryDate = addDaysToDate(new Date(), deliveryInDays);

  return {
    deliveryPromisePresentmentTitle:
      brandHandle === BrandHandle.ShopPromise
        ? 'Shop Promise'
        : 'Buy With Prime',
    minDeliveryTime: deliveryDate.toISOString(),
    maxDeliveryTime: deliveryDate.toISOString(),
    minBusinessDaysToDelivery: null,
    maxBusinessDaysToDelivery: null,
    minCalendarDaysToDelivery: deliveryInDays,
    maxCalendarDaysToDelivery: deliveryInDays,
    deliveryDates: null,
    expiresAt: new Date(Date.now() + 1000 * 60 * 60).toISOString(),
    cost: {
      amount: '0.0',
    },
    brandedPromise: {
      name: 'Shop Promise',
      handle: brandHandle,
      lightLogo: {
        src: lightLogoSrc,
        altText: 'Shop Promise',
      },
      darkLogo: {
        src: darkLogoSrc,
        altText: 'Shop Promise',
      },
    },
    reliableDateSource: true,
  };
}
